import { useState, useMemo, useEffect, useCallback } from 'react';

const isNil = (v) => v === null || v === undefined || (typeof v === 'number' && Number.isNaN(v));

const compareValues = (a, b) => {
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b);
    return a < b ? -1 : a > b ? 1 : 0;
};

// getSortValue(row, key) 가 돌려주는 원값(타임스탬프·숫자 등)으로 정렬. null/undefined 는 방향과 무관하게 항상 뒤.
// 같은 값이면 원래 순서 유지(안정 정렬).
export const sortRows = (rows, sortKey, sortDir, getSortValue) => {
    const list = Array.isArray(rows) ? rows : [];
    if (!sortKey || typeof getSortValue !== 'function') return list;
    const dir = sortDir === 'desc' ? -1 : 1;
    return list
        .map((row, index) => ({ row, index, value: getSortValue(row, sortKey) }))
        .sort((x, y) => {
            const xNil = isNil(x.value);
            const yNil = isNil(y.value);
            if (xNil || yNil) {
                if (xNil && yNil) return x.index - y.index;
                return xNil ? 1 : -1;
            }
            const c = compareValues(x.value, y.value) * dir;
            return c !== 0 ? c : x.index - y.index;
        })
        .map((e) => e.row);
};

export const paginateRows = (rows, page, pageSize) => {
    const list = Array.isArray(rows) ? rows : [];
    const size = Math.max(1, parseInt(pageSize, 10) || 10);
    const pg = Math.max(1, parseInt(page, 10) || 1);
    return list.slice((pg - 1) * size, pg * size);
};

const clampPage = (page, total, size) => {
    const maxPage = Math.max(1, Math.ceil(total / size));
    return Math.min(Math.max(1, parseInt(page, 10) || 1), maxPage);
};

const useClientTable = (rows, { getSortValue, defaultSort, defaultPageSize = 10 } = {}) => {
    const [sortKey, setSortKey] = useState(defaultSort?.key ?? null);
    const [sortDir, setSortDir] = useState(defaultSort?.dir === 'desc' ? 'desc' : 'asc');
    const [rawPage, setRawPage] = useState(1);
    const [pageSize, setPageSizeState] = useState(Math.max(1, parseInt(defaultPageSize, 10) || 10));

    const list = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
    const total = list.length;

    const sorted = useMemo(
        () => sortRows(list, sortKey, sortDir, getSortValue),
        [list, sortKey, sortDir, getSortValue]
    );

    // 행 수·pageSize 가 바뀌어도 범위를 벗어난 페이지가 렌더되지 않도록 렌더 값 자체를 클램프
    const page = clampPage(rawPage, total, pageSize);
    useEffect(() => {
        if (page !== rawPage) setRawPage(page);
    }, [page, rawPage]);

    const pageRows = useMemo(() => paginateRows(sorted, page, pageSize), [sorted, page, pageSize]);

    const toggleSort = useCallback((key) => {
        if (key === sortKey) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
        setRawPage(1);
    }, [sortKey]);

    const setPage = useCallback((p) => setRawPage(Math.max(1, parseInt(p, 10) || 1)), []);

    const setPageSize = useCallback((size) => {
        setPageSizeState(Math.max(1, parseInt(size, 10) || 10));
        setRawPage(1);
    }, []);

    return { pageRows, sortKey, sortDir, toggleSort, page, setPage, pageSize, setPageSize, total };
};

export default useClientTable;
